import { useController } from '@aha-app/mvc';
import React, { useRef, useState } from 'react';
import { type Instance as TippyInstance } from 'tippy.js/index';
import WhiteboardController from '../controllers/WhiteboardController';
import { isShapeSmartRecord } from '../objects/guards';
import ToolbarMenu from '../toolbars/ToolbarMenu';
import ToolButton from '../toolbars/ToolButton';
import WithHint from '../toolbars/WithHint';
import Tool, {
  type IToolAvailableInput,
  type ToolButtonComponent,
} from './Tool';
import type Shape from '../objects/Shape';
import ShapeSmartRecord, {
  smartShapeMenuIcon,
} from '../shapes/ShapeSmartRecord';
import { type SmartRecordCreatableType } from '../types';

interface PopupProps {
  currentRecordType: string;
  updateRecordType: (recordType: SmartRecordCreatableType) => void;
}

const Popup: React.FC<PopupProps> = ({
  updateRecordType,
  currentRecordType,
}) => {
  const recordTypes = Object.keys(ShapeSmartRecord.recordTypes);

  return (
    <ToolbarMenu columns={Math.min(recordTypes.length, 8)}>
      {recordTypes.map((recordType: SmartRecordCreatableType) => (
        <WithHint
          key={recordType}
          name={ShapeSmartRecord.terminologyFor(recordType)}
        >
          <ToolButton
            active={recordType === currentRecordType}
            onClick={() => updateRecordType(recordType)}
          >
            <i className={smartShapeMenuIcon(recordType)} />
          </ToolButton>
        </WithHint>
      ))}
    </ToolbarMenu>
  );
};

const SmartRecordTypeButton: ToolButtonComponent = ({ selection, tool }) => {
  const controller = useController(WhiteboardController);
  const obj: Shape<ShapeSmartRecord> = selection.firstObject();
  const [currentRecordType, setCurrentRecordType] = useState(obj.recordType!);

  const tippyRef = useRef<TippyInstance>();

  const updateRecordType = (newRecordType: SmartRecordCreatableType) => {
    tippyRef.current?.hide();
    setCurrentRecordType(newRecordType);
    selection.setOnObjects('recordType', newRecordType);
    ToolSmartRecordType.toolActionComplete({ controller });
  };

  return (
    <ToolButton
      popup={
        <Popup
          updateRecordType={updateRecordType}
          currentRecordType={currentRecordType}
        />
      }
      tippyRef={tippyRef}
      id='shapeChange'
      tool={tool}
    >
      <i className='fa-regular fa-swap-arrows' />
    </ToolButton>
  );
};

class ToolSmartRecordType extends Tool {
  static toolName = 'Change record type';

  static available({ selection }: IToolAvailableInput): boolean {
    const objects = selection?.getObjects();
    return !!objects?.every(obj => isShapeSmartRecord(obj));
  }

  static buttonComponent() {
    return SmartRecordTypeButton;
  }
}

export default ToolSmartRecordType;
